import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Eye, EyeOff, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { checkIsAdmin } from "@/lib/blog.functions";
import { kbAdminOverview, kbSetPublished, kbDeleteArticle } from "@/lib/kb.functions";

export const Route = createFileRoute("/admin/kunskap")({
  head: () => ({
    meta: [{ title: "Admin · Kunskapsbank" }, { name: "robots", content: "noindex" }],
  }),
  beforeLoad: async () => {
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) throw redirect({ to: "/login" });
    const { isAdmin } = await checkIsAdmin();
    if (!isAdmin) throw redirect({ to: "/" });
  },
  component: AdminKunskap,
});

function AdminKunskap() {
  const qc = useQueryClient();
  const overview = useServerFn(kbAdminOverview);
  const setPublished = useServerFn(kbSetPublished);
  const remove = useServerFn(kbDeleteArticle);

  const { data } = useQuery({
    queryKey: ["kb_admin"],
    queryFn: async () => await overview(),
  });

  const articles = (data?.articles ?? []) as any[];
  const keywords = (data?.keywords ?? []) as any[];
  const counts = (data?.counts ?? {}) as Record<string, number>;

  async function toggle(id: string, published: boolean) {
    try {
      await setPublished({ data: { id, published } });
      toast.success(published ? "Publicerad" : "Avpublicerad");
      qc.invalidateQueries({ queryKey: ["kb_admin"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Fel");
    }
  }

  async function del(id: string) {
    if (!confirm("Ta bort artikeln permanent?")) return;
    try {
      await remove({ data: { id } });
      toast.success("Borttagen");
      qc.invalidateQueries({ queryKey: ["kb_admin"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Fel");
    }
  }

  return (
    <section className="container-edge max-w-6xl py-16">
      <p className="eyebrow">Admin</p>
      <h1 className="mt-3 font-display text-4xl md:text-5xl">
        Kunskaps<span className="font-italic-display text-accent">motorn</span>.
      </h1>

      <div className="mt-8 grid gap-4 sm:grid-cols-4">
        {[
          ["I kö", counts.queued ?? 0],
          ["Skriver", counts.writing ?? 0],
          ["Klara", counts.done ?? 0],
          ["Avvisade", counts.rejected ?? 0],
        ].map(([label, value]) => (
          <div key={String(label)} className="rounded-3xl border border-border bg-card p-5">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
            <p className="mt-2 font-display text-3xl">{value as number}</p>
          </div>
        ))}
      </div>

      <h2 className="mt-14 font-display text-2xl">Artiklar ({articles.length})</h2>
      <div className="mt-5 divide-y divide-border overflow-hidden rounded-3xl border border-border bg-card">
        {articles.length === 0 && (
          <p className="p-6 text-sm text-muted-foreground">Inga artiklar genererade än.</p>
        )}
        {articles.map((a) => {
          const q = (a.quality ?? {}) as Record<string, any>;
          return (
            <div key={a.id} className="flex flex-wrap items-center justify-between gap-4 p-5">
              <div className="min-w-0 flex-1">
                <p className="truncate font-display text-lg">{a.title}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  /kunskap/{a.slug} · {a.cluster ?? "–"} ·{" "}
                  {a.published ? <span className="text-accent">publicerad</span> : "granskning"}
                </p>
                <p className="mt-1 font-mono text-[11px] text-muted-foreground">
                  {q.words ?? "?"} ord · {q.dataPoints ?? "?"} datapunkter · likhet{" "}
                  {q.similarity ?? "?"} · H2 {q.h2 ?? "?"} · länkar {q.internalLinks ?? "?"}(
                  {q.distinctInternalLinks ?? "?"} unika)
                </p>
                {Array.isArray(q.problems) && q.problems.length > 0 && (
                  <p className="mt-1 text-[11px] text-destructive">{q.problems.join(" · ")}</p>
                )}
              </div>
              <div className="flex shrink-0 gap-2">
                <a
                  href={`/kunskap/${a.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-full border border-border bg-background px-4 py-2 text-xs hover:bg-secondary"
                >
                  Visa
                </a>
                <button
                  onClick={() => toggle(a.id, !a.published)}
                  className="rounded-full border border-border bg-background p-2.5 hover:bg-secondary"
                  aria-label={a.published ? "Avpublicera" : "Publicera"}
                >
                  {a.published ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
                <button
                  onClick={() => del(a.id)}
                  className="rounded-full border border-border bg-destructive p-2.5 text-destructive-foreground hover:opacity-90"
                  aria-label="Ta bort"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <h2 className="mt-14 font-display text-2xl">Sökordskö ({keywords.length})</h2>
      <div className="mt-5 divide-y divide-border overflow-hidden rounded-3xl border border-border bg-card">
        {keywords.slice(0, 60).map((k) => (
          <div key={k.id} className="flex items-center justify-between gap-4 p-4 text-sm">
            <div className="min-w-0">
              <p className="truncate">{k.keyword}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {k.cluster ?? "–"} · prio {k.priority} · {k.status} · {k.source ?? "seed"}
                {k.attempts > 0 ? ` · ${k.attempts} försök` : ""}
              </p>
              {k.last_error && (
                <p className="mt-0.5 text-[11px] text-destructive">{k.last_error}</p>
              )}
            </div>
          </div>
        ))}
        {keywords.length === 0 && (
          <p className="p-6 text-sm text-muted-foreground">
            Kön är tom – kör hooken /api/public/hooks/kb-harvest.
          </p>
        )}
      </div>

      <div className="mt-8">
        <Link to="/" className="text-sm text-muted-foreground hover:text-accent">
          ← Till hem
        </Link>
      </div>
    </section>
  );
}
